"use client";
import LayoutBanner from "@/components/customLayoutServices/LayoutBanner";
import { useTranslations } from "next-intl";
import { Phone, CreditCard, Clock, Signal, Check, Wifi } from "lucide-react";

const WIFI_DATA = {
  networkName: "Airport_Tocumen",
  support: "+(507) 6904-1644",
  paymentMethods: ["Visa", "MasterCard", "Amex"],
  plans: [
    { price: 4.0, duration: 1, popular: false },
    { price: 6.0, duration: 2, popular: false },
    { price: 10.0, duration: 4, popular: true },
    { price: 15.0, duration: 24, popular: false },
  ],
  featureKeys: ["feature1", "feature2", "feature3", "feature4"] as const,
};

const NAVY = "#102542";

export default function WifiPage() {
  const t = useTranslations("wifi");

  return (
    <LayoutBanner
      bannerSrc="https://galeria.tocumenaero.com/gallery/api/Panor%C3%A1micas%20internas/9M1A0559.jpg"
      cardTitle="WiFi"
    >
      <section className="w-full min-h-screen pb-24 flex flex-col items-center ">
        <div className="w-full max-w-6xl px-4 md:px-8 py-12 md:py-16">
          {/* Hero - 1 Hora Gratis */}
          <div className="mb-12">
            <div
              className="relative rounded-2xl overflow-hidden shadow-lg"
              style={{ backgroundColor: NAVY }}
            >
              <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
                <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-white blur-3xl" />
                <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-white blur-3xl" />
              </div>
              <div className="relative px-6 py-6 md:px-10 md:py-7 flex flex-col md:flex-row items-center justify-evenly gap-5 text-center md:text-left">
                <div className="flex items-center gap-4">
                  <div className="hidden md:flex w-12 h-12 rounded-xl bg-white/10 border border-white/20 items-center justify-center backdrop-blur-sm shrink-0">
                    <Wifi className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-white/60 text-[10px] uppercase tracking-widest font-medium mb-1">
                      {t("conectateYa") || "Conéctate ahora"}
                    </p>
                    <div className="flex items-baseline justify-center md:justify-start gap-1.5">
                      <span className="text-4xl md:text-5xl font-black text-white leading-none">
                        1
                      </span>
                      <span className="text-lg md:text-xl font-semibold text-white/80">
                        {t("hora")}
                      </span>
                      <span className="ml-2 text-xs md:text-sm font-bold tracking-wider text-[#102542] bg-white px-2.5 py-1 rounded-full">
                        {t("gratis") || "GRATIS"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="hidden md:block h-12 w-px bg-white/15" />
                <div className="max-w-xs ">
                  <p className="text-white/60 text-[10px] uppercase tracking-widest font-medium mb-1">
                    {t("nombre")}
                  </p>
                  <p className="text-white text-lg md:text-xl font-bold font-mono tracking-tight">
                    {WIFI_DATA.networkName}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Cards de información */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-20">
            {/* Network Name */}
            <div className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-md border border-gray-200 hover:border-[#102542]/40 transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${NAVY}15`, color: NAVY }}
                >
                  <Signal className="w-5 h-5" />
                </div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t("nombre")}
                </h3>
              </div>
              <p
                className="text-xl font-bold break-all"
                style={{ color: NAVY }}
              >
                {WIFI_DATA.networkName}
              </p>
            </div>

            {/* Payment Methods */}
            <div className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-md border border-gray-200 hover:border-[#102542]/40 transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${NAVY}15`, color: NAVY }}
                >
                  <CreditCard className="w-5 h-5" />
                </div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t("metodo")}
                </h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {WIFI_DATA.paymentMethods.map((method) => (
                  <span
                    key={method}
                    className="px-3 py-1 text-sm font-medium bg-gray-100 text-gray-700 rounded-full border border-gray-200"
                  >
                    {method}
                  </span>
                ))}
              </div>
            </div>

            {/* Support */}
            <div className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-md border border-gray-200 hover:border-[#102542]/40 transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${NAVY}15`, color: NAVY }}
                >
                  <Phone className="w-5 h-5" />
                </div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t("soporte")}
                </h3>
              </div>
              <a
                href={`tel:${WIFI_DATA.support}`}
                className="text-xl font-bold hover:opacity-70 transition-opacity"
                style={{ color: NAVY }}
              >
                {WIFI_DATA.support}
              </a>
            </div>
          </div>

          {/* Tarifas */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2
                className="text-3xl md:text-4xl font-bold mb-3"
                style={{ color: NAVY }}
              >
                {t("tarifas")}
              </h2>
              <div
                className="w-12 h-0.5 mx-auto mb-4 rounded-full"
                style={{ backgroundColor: NAVY }}
              />
              <p className="text-gray-500 max-w-xl mx-auto">
                {t("tarifasDescripcion")}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {WIFI_DATA.plans.map((plan, index) => {
                const isPopular = plan.popular;
                return (
                  <div
                    key={index}
                    className={`relative bg-white rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 ${
                      isPopular
                        ? "border-2 shadow-xl"
                        : "border border-gray-200 shadow-sm hover:shadow-lg"
                    }`}
                    style={isPopular ? { borderColor: NAVY } : undefined}
                  >
                    {isPopular && (
                      <div
                        className="absolute top-4 right-4 text-[10px] uppercase tracking-widest font-bold text-white px-2.5 py-1 rounded-full"
                        style={{ backgroundColor: NAVY }}
                      >
                        Popular
                      </div>
                    )}

                    <div className="p-6 border-b border-gray-100">
                      <div className="flex items-center gap-2 mb-4 text-gray-500">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          {plan.duration}{" "}
                          {plan.duration === 1 ? t("hora") : t("horas")}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span
                          className="text-sm font-semibold"
                          style={{ color: NAVY }}
                        >
                          B/.
                        </span>
                        <span
                          className="text-5xl font-black leading-none"
                          style={{ color: NAVY }}
                        >
                          {plan.price.toFixed(2).split(".")[0]}
                        </span>
                        <span
                          className="text-lg font-bold"
                          style={{ color: NAVY }}
                        >
                          .{plan.price.toFixed(2).split(".")[1]}
                        </span>
                      </div>
                    </div>

                    <div className="p-5">
                      <ul className="space-y-2.5">
                        {WIFI_DATA.featureKeys
                          .slice(0, 2 + index)
                          .map((featureKey, i) => (
                            <li
                              key={i}
                              className="flex items-start gap-2.5 text-sm text-gray-600"
                            >
                              <Check
                                className="w-4 h-4 mt-0.5 shrink-0"
                                style={{ color: NAVY }}
                              />
                              <span>{t(featureKey)}</span>
                            </li>
                          ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Instrucciones de conexión */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 md:p-12">
            <div className="text-center mb-10">
              <h3
                className="text-2xl md:text-3xl font-bold mb-3"
                style={{ color: NAVY }}
              >
                {t("comoConectarse")}
              </h3>
              <div
                className="w-12 h-0.5 mx-auto rounded-full"
                style={{ backgroundColor: NAVY }}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
              {/* Línea conectora desktop */}
              <div
                className="hidden md:block absolute top-7 left-[16.66%] right-[16.66%] h-px bg-gray-200"
                aria-hidden
              />
              {[
                {
                  step: 1,
                  title: t("paso1Titulo"),
                  description: t("paso1Descripcion"),
                },
                {
                  step: 2,
                  title: t("paso2Titulo"),
                  description: t("paso2Descripcion"),
                },
                {
                  step: 3,
                  title: t("paso3Titulo"),
                  description: t("paso3Descripcion"),
                },
              ].map((item) => (
                <div
                  key={item.step}
                  className="relative text-center flex flex-col items-center"
                >
                  <div
                    className="relative z-10 w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-lg mb-4 shadow-md"
                    style={{ backgroundColor: NAVY }}
                  >
                    {item.step}
                  </div>
                  <h4
                    className="font-bold text-lg mb-2"
                    style={{ color: NAVY }}
                  >
                    {item.title}
                  </h4>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-xs">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </LayoutBanner>
  );
}
